import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import Spinner from '@/components/spinner/Spinner';

type LottieSource = object;
type AnswerBoxSource = LottieSource | string;

type ThemeLottieVariants = {
	mobile: LottieSource;
	tablet?: LottieSource;
	tabletPortrait?: LottieSource;
	tabletLandscape?: LottieSource;
	desktop: LottieSource;
};

export type LaneChooserTheme = {
	key: LANE_CHOOSER_THEME_KEYS;
	images: {
		sideElements: string[];
		character: {
			jump: React.ComponentType<React.SVGProps<SVGSVGElement>>;
		};
	};
	animations: {
		answerBoxes: {
			left: AnswerBoxSource;
			middle: AnswerBoxSource;
			right: AnswerBoxSource;
		};
		character: {
			default: LottieSource;
			correct: LottieSource;
		};
		endScreen: {
			success: ThemeLottieVariants;
			failed: ThemeLottieVariants;
		};
		startScreen: {
			mapUnfold: LottieSource;
			background?: ThemeLottieVariants;
		};
	};
};

export enum LANE_CHOOSER_THEME_KEYS {
	LAKE = 'lake',
	FOREST = 'forest',
	MOUNTAIN = 'mountain',
	DESERT = 'desert',
	PARK = 'park',
}

export const defaultThemeKey = LANE_CHOOSER_THEME_KEYS.LAKE;

// Dynamic theme loaders - each theme is loaded only when needed
const themeLoaders: Record<LANE_CHOOSER_THEME_KEYS, () => Promise<LaneChooserTheme>> = {
	[LANE_CHOOSER_THEME_KEYS.LAKE]: async () => {
		const [
			{ default: LakeSide1 },
			{ default: LakeSide2 },
			{ default: LakeSide3 },
			{ default: LakeSide4 },
			{ default: LakeCharacterJump },
			{ default: LakeLeftBox },
			{ default: LakeMiddleBox },
			{ default: LakeRightBox },
			{ default: LakeCharacterDefault },
			{ default: LakeCharacterCorrect },
			{ default: LakeEndScreenSuccessMobile },
			{ default: LakeEndScreenSuccessTablet },
			{ default: LakeEndScreenSuccessDesktop },
			{ default: LakeEndScreenFailedMobile },
			{ default: LakeEndScreenFailedTablet },
			{ default: LakeEndScreenFailedDesktop },
			{ default: LakeMapUnfold },
		] = await Promise.all([
			import('@/playlist/games/laneChooser/assets/images/lake/ducks.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/frog.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/thatch.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/watergrass.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/character-jump.svg?react'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/left-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/middle-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/right-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/character-default.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/character-correct.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/endscreen-success-mobile.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/endscreen-success-tablet.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/lake/endscreen-success-desktop.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/lake/endscreen-failed-mobile.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/endscreen-failed-tablet.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/endscreen-failed-desktop.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/map-unfold.json'),
		]);

		return {
			key: LANE_CHOOSER_THEME_KEYS.LAKE,
			images: {
				sideElements: [LakeSide1, LakeSide2, LakeSide3, LakeSide4],
				character: { jump: LakeCharacterJump },
			},
			animations: {
				answerBoxes: {
					left: LakeLeftBox,
					middle: LakeMiddleBox,
					right: LakeRightBox,
				},
				character: {
					default: LakeCharacterDefault,
					correct: LakeCharacterCorrect,
				},
				endScreen: {
					success: {
						mobile: LakeEndScreenSuccessMobile,
						tabletPortrait: LakeEndScreenSuccessTablet,
						tabletLandscape: LakeEndScreenSuccessDesktop,
						desktop: LakeEndScreenSuccessDesktop,
					},
					failed: {
						mobile: LakeEndScreenFailedMobile,
						tabletPortrait: LakeEndScreenFailedTablet,
						tabletLandscape: LakeEndScreenFailedDesktop,
						desktop: LakeEndScreenFailedDesktop,
					},
				},
				startScreen: {
					mapUnfold: LakeMapUnfold,
				},
			},
		};
	},

	[LANE_CHOOSER_THEME_KEYS.FOREST]: async () => {
		const [
			{ default: LakeSide1 },
			{ default: LakeSide2 },
			{ default: LakeSide3 },
			{ default: LakeSide4 },
			{ default: ForestCharacterJump },
			{ default: LakeLeftBox },
			{ default: LakeMiddleBox },
			{ default: LakeRightBox },
			{ default: ForestCharacterDefault },
			{ default: ForestCharacterCorrect },
			{ default: ForestEndScreenSuccessMobile },
			{ default: ForestEndScreenSuccessTabletPortrait },
			{ default: ForestEndScreenSuccessTabletLandscape },
			{ default: ForestEndScreenSuccessDesktop },
			{ default: ForestEndScreenFailedMobile },
			{ default: ForestEndScreenFailedTabletPortrait },
			{ default: ForestEndScreenFailedTabletLandscape },
			{ default: ForestEndScreenFailedDesktop },
			{ default: ForestMapUnfold },
			{ default: ForestStartScreenMobile },
			{ default: ForestStartScreenTabletPortrait },
			{ default: ForestStartScreenTabletLandscape },
			{ default: ForestStartScreenDesktop },
		] = await Promise.all([
			import('@/playlist/games/laneChooser/assets/images/lake/ducks.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/frog.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/thatch.svg'),
			import('@/playlist/games/laneChooser/assets/images/lake/watergrass.svg'),
			import('@/playlist/games/laneChooser/assets/images/forest/character-jump.svg?react'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/left-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/middle-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/lake/right-box.json'),
			import('@/playlist/games/laneChooser/assets/lottie/forest/character-default.json'),
			import('@/playlist/games/laneChooser/assets/lottie/forest/character-correct.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-success-mobile.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-success-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-success-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-success-desktop.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-failed-mobile.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-failed-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-failed-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/endscreen-failed-desktop.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/forest/map-unfold.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/start-screen-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/start-screen-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/forest/start-screen-tablet-landscape.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/forest/start-screen-desktop.json'),
		]);

		return {
			key: LANE_CHOOSER_THEME_KEYS.FOREST,
			images: {
				sideElements: [LakeSide1, LakeSide2, LakeSide3, LakeSide4],
				character: { jump: ForestCharacterJump },
			},
			animations: {
				answerBoxes: {
					left: LakeLeftBox,
					middle: LakeMiddleBox,
					right: LakeRightBox,
				},
				character: {
					default: ForestCharacterDefault,
					correct: ForestCharacterCorrect,
				},
				endScreen: {
					success: {
						mobile: ForestEndScreenSuccessMobile,
						tabletPortrait: ForestEndScreenSuccessTabletPortrait,
						tabletLandscape: ForestEndScreenSuccessTabletLandscape,
						desktop: ForestEndScreenSuccessDesktop,
					},
					failed: {
						mobile: ForestEndScreenFailedMobile,
						tabletPortrait: ForestEndScreenFailedTabletPortrait,
						tabletLandscape: ForestEndScreenFailedTabletLandscape,
						desktop: ForestEndScreenFailedDesktop,
					},
				},
				startScreen: {
					mapUnfold: ForestMapUnfold,
					background: {
						mobile: ForestStartScreenMobile,
						tabletPortrait: ForestStartScreenTabletPortrait,
						tabletLandscape: ForestStartScreenTabletLandscape,
						desktop: ForestStartScreenDesktop,
					},
				},
			},
		};
	},

	[LANE_CHOOSER_THEME_KEYS.MOUNTAIN]: async () => {
		const [
			{ default: MountainPenguin },
			{ default: MountainSnowman },
			{ default: MountainFlag },
			{ default: MountainCharacterJump },
			{ default: MountainLeftBox },
			{ default: MountainMiddleBox },
			{ default: MountainRightBox },
			{ default: MountainCharacterDefault },
			{ default: MountainCharacterCorrect },
			{ default: MountainEndScreenSuccessMobile },
			{ default: MountainEndScreenSuccessTabletPortrait },
			{ default: MountainEndScreenSuccessTabletLandscape },
			{ default: MountainEndScreenSuccessDesktop },
			{ default: MountainEndScreenFailedMobile },
			{ default: MountainEndScreenFailedTabletPortrait },
			{ default: MountainEndScreenFailedTabletLandscape },
			{ default: MountainEndScreenFailedDesktop },
			{ default: MountainMapUnfold },
			{ default: MountainStartScreenMobile },
			{ default: MountainStartScreenTabletPortrait },
			{ default: MountainStartScreenTabletLandscape },
			{ default: MountainStartScreenDesktop },
		] = await Promise.all([
			import('@/playlist/games/laneChooser/assets/images/mountain/penguin.svg'),
			import('@/playlist/games/laneChooser/assets/images/mountain/snowman.svg'),
			import('@/playlist/games/laneChooser/assets/images/mountain/flag.svg'),
			import('@/playlist/games/laneChooser/assets/images/mountain/character-jump.svg?react'),
			import('@/playlist/games/laneChooser/assets/images/mountain/left-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/mountain/middle-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/mountain/right-box.svg'),
			import('@/playlist/games/laneChooser/assets/lottie/mountain/character-default.json'),
			import('@/playlist/games/laneChooser/assets/lottie/mountain/character-correct.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-success-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-success-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-success-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-success-desktop.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-failed-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-failed-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-failed-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/endscreen-failed-desktop.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/mountain/map-unfold.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/start-screen-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/start-screen-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/mountain/start-screen-tablet-landscape.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/mountain/start-screen-desktop.json'),
		]);

		return {
			key: LANE_CHOOSER_THEME_KEYS.MOUNTAIN,
			images: {
				sideElements: [MountainPenguin, MountainSnowman, MountainFlag],
				character: { jump: MountainCharacterJump },
			},
			animations: {
				answerBoxes: {
					left: MountainLeftBox,
					middle: MountainMiddleBox,
					right: MountainRightBox,
				},
				character: {
					default: MountainCharacterDefault,
					correct: MountainCharacterCorrect,
				},
				endScreen: {
					success: {
						mobile: MountainEndScreenSuccessMobile,
						tabletPortrait: MountainEndScreenSuccessTabletPortrait,
						tabletLandscape: MountainEndScreenSuccessTabletLandscape,
						desktop: MountainEndScreenSuccessDesktop,
					},
					failed: {
						mobile: MountainEndScreenFailedMobile,
						tabletPortrait: MountainEndScreenFailedTabletPortrait,
						tabletLandscape: MountainEndScreenFailedTabletLandscape,
						desktop: MountainEndScreenFailedDesktop,
					},
				},
				startScreen: {
					mapUnfold: MountainMapUnfold,
					background: {
						mobile: MountainStartScreenMobile,
						tabletPortrait: MountainStartScreenTabletPortrait,
						tabletLandscape: MountainStartScreenTabletLandscape,
						desktop: MountainStartScreenDesktop,
					},
				},
			},
		};
	},

	[LANE_CHOOSER_THEME_KEYS.DESERT]: async () => {
		const [
			{ default: DesertSnake },
			{ default: DesertTumbleweed },
			{ default: DesertStones },
			{ default: DesertGrass },
			{ default: DesertCharacterJump },
			{ default: DesertLeftBox },
			{ default: DesertMiddleBox },
			{ default: DesertRightBox },
			{ default: DesertCharacterDefault },
			{ default: DesertCharacterCorrect },
			{ default: DesertEndScreenSuccessMobile },
			{ default: DesertEndScreenSuccessTabletPortrait },
			{ default: DesertEndScreenSuccessTabletLandscape },
			{ default: DesertEndScreenSuccessDesktop },
			{ default: DesertEndScreenFailedMobile },
			{ default: DesertEndScreenFailedTabletPortrait },
			{ default: DesertEndScreenFailedTabletLandscape },
			{ default: DesertEndScreenFailedDesktop },
			{ default: DesertMapUnfold },
			{ default: DesertStartScreenMobilePortrait },
			{ default: DesertStartScreenTabletPortrait },
			{ default: DesertStartScreenTabletLandscape },
			{ default: DesertStartScreenDesktop },
		] = await Promise.all([
			import('@/playlist/games/laneChooser/assets/images/desert/snake.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/tumbleweed.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/stones.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/grass.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/character-jump.svg?react'),
			import('@/playlist/games/laneChooser/assets/images/desert/left-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/middle-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/desert/right-box.svg'),
			import('@/playlist/games/laneChooser/assets/lottie/desert/character-default.json'),
			import('@/playlist/games/laneChooser/assets/lottie/desert/character-correct.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-success-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-success-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-success-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-success-desktop.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-failed-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-failed-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-failed-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/endscreen-failed-desktop.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/desert/map-unfold.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/start-screen-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/start-screen-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/desert/start-screen-tablet-landscape.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/desert/start-screen-desktop.json'),
		]);

		return {
			key: LANE_CHOOSER_THEME_KEYS.DESERT,
			images: {
				sideElements: [DesertSnake, DesertTumbleweed, DesertStones, DesertGrass],
				character: { jump: DesertCharacterJump },
			},
			animations: {
				answerBoxes: {
					left: DesertLeftBox,
					middle: DesertMiddleBox,
					right: DesertRightBox,
				},
				character: {
					default: DesertCharacterDefault,
					correct: DesertCharacterCorrect,
				},
				endScreen: {
					success: {
						mobile: DesertEndScreenSuccessMobile,
						tabletPortrait: DesertEndScreenSuccessTabletPortrait,
						tabletLandscape: DesertEndScreenSuccessTabletLandscape,
						desktop: DesertEndScreenSuccessDesktop,
					},
					failed: {
						mobile: DesertEndScreenFailedMobile,
						tabletPortrait: DesertEndScreenFailedTabletPortrait,
						tabletLandscape: DesertEndScreenFailedTabletLandscape,
						desktop: DesertEndScreenFailedDesktop,
					},
				},
				startScreen: {
					mapUnfold: DesertMapUnfold,
					background: {
						mobile: DesertStartScreenMobilePortrait,
						tabletPortrait: DesertStartScreenTabletPortrait,
						tabletLandscape: DesertStartScreenTabletLandscape,
						desktop: DesertStartScreenDesktop,
					},
				},
			},
		};
	},

	[LANE_CHOOSER_THEME_KEYS.PARK]: async () => {
		const [
			{ default: ParkSkateboard },
			{ default: ParkSoccerball },
			{ default: ParkPylon },
			{ default: ParkTrashbin },
			{ default: ParkCharacterJump },
			{ default: ParkLeftBox },
			{ default: ParkMiddleBox },
			{ default: ParkRightBox },
			{ default: ParkCharacterDefault },
			{ default: ParkCharacterCorrect },
			{ default: ParkEndScreenSuccessMobile },
			{ default: ParkEndScreenSuccessTabletPortrait },
			{ default: ParkEndScreenSuccessTabletLandscape },
			{ default: ParkEndScreenSuccessDesktop },
			{ default: ParkEndScreenFailedMobile },
			{ default: ParkEndScreenFailedTabletPortrait },
			{ default: ParkEndScreenFailedTabletLandscape },
			{ default: ParkEndScreenFailedDesktop },
			{ default: ParkMapUnfold },
			{ default: ParkStartScreenMobile },
			{ default: ParkStartScreenTabletPortrait },
			{ default: ParkStartScreenTabletLandscape },
			{ default: ParkStartScreenDesktop },
		] = await Promise.all([
			import('@/playlist/games/laneChooser/assets/images/park/skateboard.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/soccerball.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/pylon.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/trashbin.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/character-jump.svg?react'),
			import('@/playlist/games/laneChooser/assets/images/park/left-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/middle-box.svg'),
			import('@/playlist/games/laneChooser/assets/images/park/right-box.svg'),
			import('@/playlist/games/laneChooser/assets/lottie/park/character-default.json'),
			import('@/playlist/games/laneChooser/assets/lottie/park/character-correct.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-success-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-success-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-success-tablet-landscape.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-success-desktop.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-failed-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-failed-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/endscreen-failed-tablet-landscape.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/park/endscreen-failed-desktop.json'),
			import('@/playlist/games/laneChooser/assets/lottie/park/map-unfold.json'),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/start-screen-mobile-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/start-screen-tablet-portrait.json'
			),
			import(
				'@/playlist/games/laneChooser/assets/lottie/park/start-screen-tablet-landscape.json'
			),
			import('@/playlist/games/laneChooser/assets/lottie/park/start-screen-desktop.json'),
		]);

		return {
			key: LANE_CHOOSER_THEME_KEYS.PARK,
			images: {
				sideElements: [ParkSkateboard, ParkSoccerball, ParkPylon, ParkTrashbin],
				character: { jump: ParkCharacterJump },
			},
			animations: {
				answerBoxes: {
					left: ParkLeftBox,
					middle: ParkMiddleBox,
					right: ParkRightBox,
				},
				character: {
					default: ParkCharacterDefault,
					correct: ParkCharacterCorrect,
				},
				endScreen: {
					success: {
						mobile: ParkEndScreenSuccessMobile,
						tabletPortrait: ParkEndScreenSuccessTabletPortrait,
						tabletLandscape: ParkEndScreenSuccessTabletLandscape,
						desktop: ParkEndScreenSuccessDesktop,
					},
					failed: {
						mobile: ParkEndScreenFailedMobile,
						tabletPortrait: ParkEndScreenFailedTabletPortrait,
						tabletLandscape: ParkEndScreenFailedTabletLandscape,
						desktop: ParkEndScreenFailedDesktop,
					},
				},
				startScreen: {
					mapUnfold: ParkMapUnfold,
					background: {
						mobile: ParkStartScreenMobile,
						tabletPortrait: ParkStartScreenTabletPortrait,
						tabletLandscape: ParkStartScreenTabletLandscape,
						desktop: ParkStartScreenDesktop,
					},
				},
			},
		};
	},
};

// Cache for loaded themes
const themeCache = new Map<LANE_CHOOSER_THEME_KEYS, LaneChooserTheme>();

export const loadLaneChooserTheme = async (
	key: LANE_CHOOSER_THEME_KEYS = defaultThemeKey,
): Promise<LaneChooserTheme> => {
	const cached = themeCache.get(key);
	if (cached) return cached;

	const loader = themeLoaders[key] || themeLoaders[defaultThemeKey];
	const theme = await loader();
	themeCache.set(key, theme);
	return theme;
};

export const getLaneChooserThemeKey = (key?: string | null): LANE_CHOOSER_THEME_KEYS => {
	if (!key) return defaultThemeKey;
	const normalized = key.trim().toLowerCase() as LANE_CHOOSER_THEME_KEYS;
	return Object.values(LANE_CHOOSER_THEME_KEYS).includes(normalized)
		? normalized
		: defaultThemeKey;
};

const LaneChooserThemeContext = createContext<LaneChooserTheme | null>(null);

export const useLaneChooserTheme = (): LaneChooserTheme => {
	const theme = useContext(LaneChooserThemeContext);
	if (!theme) {
		throw new Error('useLaneChooserTheme must be used within LaneChooserThemeProvider');
	}
	return theme;
};

export const LaneChooserThemeProvider = ({
	themeKey,
	children,
}: {
	themeKey?: string | null;
	children: React.ReactNode;
}) => {
	const [theme, setTheme] = useState<LaneChooserTheme | null>(null);
	const normalizedKey = useMemo(() => getLaneChooserThemeKey(themeKey), [themeKey]);

	useEffect(() => {
		let mounted = true;
		loadLaneChooserTheme(normalizedKey).then((loadedTheme) => {
			if (mounted) {
				setTheme(loadedTheme);
			}
		});
		return () => {
			mounted = false;
		};
	}, [normalizedKey]);

	if (!theme) {
		return <Spinner classname="spinner--page" />;
	}

	return (
		<LaneChooserThemeContext.Provider value={theme}>
			{children}
		</LaneChooserThemeContext.Provider>
	);
};
